import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CartService } from 'app/main/services/cart.service';
import { BaseCartDirective } from 'app/main/cart.base';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl, FormArray } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { CheckoutService } from 'app/main/services/checkout.service';
import { AppService } from 'app/main/services/app.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
	selector: 'checkout-page',
	templateUrl: './checkout.component.html',
	styleUrls: ['./checkout.component.scss'],
	providers: [CheckoutService]
})
export class CheckoutComponent extends BaseCartDirective implements OnInit {

	@ViewChild('cardElement') cardElement: ElementRef;

	public submitBtnDisabled: boolean;
	public stripeIsLoading: boolean;
	public loadingCountries: boolean;
	public isLoading: boolean;
	public countries: any;
	public formGroup: FormGroup;

	public methodsPaymentEnabled: boolean;
	public paypalConfig: any;
	public stripeConfig: any;

	public ticket_details: boolean;

	protected swal: any;
	protected stripe: any;
	protected card: any;

	readonly cardStyle = {
		base: {
			color: '#32325d',
			fontFamily: 'Arial, sans-serif',
			fontSmoothing: 'antialiased',
			fontSize: '16px',
				'::placeholder': {
				color: '#32325d'
			}
		},
		invalid: {
			fontFamily: 'Arial, sans-serif',
			color: '#fa755a',
			iconColor: '#fa755a'
		}
	};

	constructor(
		public cartService: CartService, 
		public checkoutService: CheckoutService,
		public appService: AppService,
		private router: Router,
		private fb: FormBuilder, 
		public translate: TranslateService) {
			super(appService, translate, cartService);
		}

	initForm(): void{
		this.formGroup = this.fb.group({
			name: new FormControl('', [Validators.required]),
			surname: new FormControl('', [Validators.required]),
			email: new FormControl('', [Validators.required, Validators.email]),
			country: new FormControl('', [Validators.required]),
			tel: new FormControl('', [Validators.required]),
			p_s: new FormControl('', [Validators.required]),
			passengers_details: new FormControl(true),
			conditions: new FormGroup({
				first: new FormControl(false, [Validators.required, Validators.requiredTrue]),
				second: new FormControl(false),
				third: new FormControl(false, [Validators.required, Validators.requiredTrue])
			}),
			participants: new FormArray([])
		});
	}

	checkEnablingPayments(): boolean{
		if (!this.paypalConfig.enabled && !this.stripeConfig.enabled) return false;
		return true;
	}

	ngOnInit(): void {
		super.ngOnInit();
		// init form
		this.initForm();

		this.cartService.cartData.subscribe(cartData => {
			this.cartData = cartData; 
			// load nations only if cartdata exists
			if (cartData){
				this.loadingCountries = true;
				this.checkoutService.loadCountries()
				.pipe(takeUntil(this._unsubscribeAll))
				.subscribe((res) => {
					this.countries = res['items'];
					this.country.setValue('IT');
					this.loadingCountries = false;
				});
			}
		});

		this.submitBtnDisabled = true;

		this.appService.appData.subscribe(appData => {
			const methodPayments = appData.method_payments;
			if (!methodPayments) return;

			this.paypalConfig = methodPayments.paypal;
			this.stripeConfig = methodPayments.stripe;
			this.methodsPaymentEnabled = this.checkEnablingPayments();
	
			if (this.methodsPaymentEnabled && methodPayments.stripe.client_id){
				this.stripe = Stripe(methodPayments.stripe.client_id);
			}
		});

		this.payment_type.valueChanges.subscribe(res => {
			if (res === 'stripe'){
				this.submitBtnDisabled = true;
				this.stripeIsLoading = true;
				setTimeout(() => {
					this.initStripe();
					this.stripeIsLoading = false;
				}, 1000);
			} else {
				this.submitBtnDisabled = false;
			}
		});
	}

	initStripe(): void{
		if (this.stripe){
			const elements = this.stripe.elements();
			this.card = elements.create('card', { hidePostalCode: true, style: this.cardStyle });
			this.card.mount(this.cardElement.nativeElement);

			this.card.on('change', (event: any) => {
				this.submitBtnDisabled = event.empty || !event.complete;
				document.querySelector('#card-error').textContent = event.error ? event.error.message : '';
			});
		}
	}

	get country(): AbstractControl{
		return this.formGroup.get('country');
	}

	get payment_type(): AbstractControl{
		return this.formGroup.get('p_s');
	}

	get conditions(): FormGroup{
		return <FormGroup>this.formGroup.get('conditions');
	}

	get entityDescr(): any{
		return this.formGroup.get('name').value + ' ' + this.formGroup.get('surname').value;
	}

	get email(): any{
		return this.formGroup.get('email').value;
	}

	onSubmit(): void{
		switch(this.payment_type.value){
			case 'paypal':
				this.paypalCheckout();
				break;
			case 'stripe':
				this.stripeCheckout();
				break;
			default:
				console.error('nessun pagamento configurato');
				return;
		}
	}

	paypalCheckout(): void{
		this.isLoading = true;
		const formData = this.formGroup.getRawValue();
		formData['participants'] = this.getParticipants();
		this.checkoutService.checkout('/checkout/setup-payment', formData)
		.subscribe({
			next: res => {
				if (res) window.location.href = res.approval_link;
			},
			error: err => {
				this.isLoading = false;
				this.appService.showErrorMessageFromResponse(err);
			}
		});
	}

	stripeCheckout(): void{
		this.isLoading = true;
		this.checkoutService.checkout('/checkout/stripe-checkout')
		.subscribe({
			next: res => {
				if (res) this.payWithCard(res);
			},
			error: err => {
				this.isLoading = false;
			}
		});
	}

	payWithCard(response: any): void{
		this.stripe.confirmCardPayment(response.client_secret, {
			payment_method: {
				card: this.card,
				billing_details: {
					name: this.entityDescr,
					email: this.email
				},
			}
		}).then((res) => {
			if (res.error){
				this.showError(res.error.message);
			} else this.orderComplete(response);
		});
	}

	orderComplete = (payment_intent: any) => {
		const formData = this.formGroup.getRawValue();
		formData['participants'] = this.getParticipants();
		formData['payment_intent'] = payment_intent;
		this.checkoutService.confirmBooking(formData)
		.subscribe((cart) => {
			this.cartService.cartData.next(cart);
			this.isLoading = false;
			this.router.navigate(['/d', this.appService.domainId, 'thankyou', 'success']);
			/*Swal.fire({
				icon: 'success',
				title: this.translate.instant('Pagamento riuscito!'),
				text: this.translate.instant('Il pagamento è stato effettuato con successo'),
				confirmButtonText: 'OK',
				allowOutsideClick: false,
			}).then((result) => {
				if (result.isConfirmed) this.router.navigate(['/d', this.appService.domainId, 'thankyou']);
			});*/
		});
	}

	showError = (errorMsgText) => {
		this.isLoading = false;
		Swal.fire({
			icon: 'error',
			title: this.translate.instant('Pagamento non riuscito!'),
			text: errorMsgText,
			confirmButtonText: 'OK',
			allowOutsideClick: false,
		});
	}

	ticketDetails(event: any): void{
		this.ticket_details = !event.target.checked;

		const participants = <FormArray>this.formGroup.get('participants');
		if (participants.controls.length) participants.clear();

		const counter = {};

		if (this.ticket_details){
			for (let i = 0; i < this.cartData.num_items; i++){
				const trip_booking = this.cartData.cart_items[i].trip_booking;
				const participant_counters = trip_booking.participant_counters;

				const group: any = {};
				if (!counter[i]) counter[i] = {};

				// foreach element age
				['ADT', 'CHD', 'INF', 'SNR'].forEach(age => {
					const ageCount = participant_counters[age];
					if (!ageCount) return;

					if (!counter[i][age]) counter[i][age] = 0;
					if (!group[age]) group[age] = new FormArray([]);

					for (let j = 0; j < ageCount; j++){
						group[age].push(new FormGroup({
							name: new FormControl('', [Validators.required]),
							surname: new FormControl('', [Validators.required]),
							email: new FormControl('', [Validators.required, Validators.email]),
							country: new FormControl('', [Validators.required]),
							tel: new FormControl('')
						}));

						counter[i][age] = j + 1;
					}
				});
				// set form array
				participants.push(new FormGroup({[trip_booking.id]: new FormGroup(group)}));
			}
		}
	}

	getParticipants(): any{
		const newParticipantsData: any = {};
		const participants = <FormArray>this.formGroup.get('participants');
		participants.getRawValue().forEach(elements => {
			const keys = Object.keys(elements);
			keys.forEach(key => {
				if (!newParticipantsData[key]) newParticipantsData[key] = {};
				newParticipantsData[key] = elements[key];
			});
		});

		return newParticipantsData;
	}
}
